import { useEffect, useMemo, useState } from "react";
import { History, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Panel, PageTitle } from "@/components/common";

export default function LogAttivita() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [entity, setEntity] = useState("");

  const load = async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/audit");
      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const entities = useMemo(
    () => [...new Set(logs.map((row) => row.entity).filter(Boolean))].sort(),
    [logs]
  );

  const filteredLogs = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return logs.filter((row) => {
      if (entity && row.entity !== entity) return false;
      if (!needle) return true;

      return [
        row.user,
        row.action,
        row.entity,
        row.entity_id,
        row.field,
        row.old_value,
        row.new_value,
      ].some((value) =>
        String(value ?? "").toLowerCase().includes(needle)
      );
    });
  }, [logs, search, entity]);

  return (
    <div>
      <PageTitle
        title="Log Attività"
        subtitle="Audit System — Tracciamento Completo"
        action={
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="audit-refresh"
            className="btn-outline-gold clip-beveled-sm px-4 py-2 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </button>
        }
      />

      <Panel className="p-4 mb-4" testid="audit-filters">
        <div className="grid md:grid-cols-[minmax(0,1fr)_220px] gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--silver)]" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Cerca utente, azione, entità, campo o valore..."
              data-testid="audit-search"
              className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm pl-10 pr-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none"
            />
          </div>

          <select
            value={entity}
            onChange={(event) => setEntity(event.target.value)}
            data-testid="audit-entity-filter"
            className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none"
          >
            <option value="">Tutte le entità</option>
            {entities.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 font-rajdhani text-xs text-[var(--silver)]">
          Visualizzati{" "}
          <span className="text-[var(--gold)] font-bold">
            {filteredLogs.length}
          </span>{" "}
          di{" "}
          <span className="text-white font-bold">{logs.length}</span>{" "}
          eventi caricati
        </div>
      </Panel>

      <Panel className="overflow-x-auto" testid="audit-table">
        {loading && logs.length === 0 ? (
          <div
            className="p-8 text-center font-rajdhani text-[var(--silver)]"
            data-testid="audit-loading"
          >
            Caricamento log attività...
          </div>
        ) : filteredLogs.length === 0 ? (
          <div
            className="p-8 text-center font-rajdhani text-[var(--silver)]"
            data-testid="audit-empty"
          >
            {logs.length === 0
              ? "Nessuna attività registrata."
              : "Nessuna attività corrisponde ai filtri selezionati."}
          </div>
        ) : (
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="bg-[#0a0a0a] border-b border-[var(--gold)]/40 font-teko text-lg text-[var(--gold-light)] tracking-wide">
                <th className="text-left px-4 py-3">Data/Ora</th>
                <th className="text-left px-4 py-3">Utente</th>
                <th className="text-left px-4 py-3">Azione</th>
                <th className="text-left px-4 py-3">Entità</th>
                <th className="text-left px-4 py-3">Campo</th>
                <th className="text-left px-4 py-3">Variazione</th>
              </tr>
            </thead>

            <tbody>
              {filteredLogs.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani"
                  data-testid={`audit-row-${row.id}`}
                >
                  <td className="px-4 py-2.5 text-[var(--silver)] text-sm whitespace-nowrap">
                    {new Date(row.timestamp).toLocaleString("it-IT")}
                  </td>

                  <td className="px-4 py-2.5 text-[var(--gold)] text-sm">
                    {row.user}
                  </td>

                  <td className="px-4 py-2.5 text-white text-sm">
                    <div className="flex items-center gap-2">
                      <History className="w-3.5 h-3.5 shrink-0 text-[var(--gold)]" />
                      <span>{row.action}</span>
                    </div>
                  </td>

                  <td className="px-4 py-2.5 text-[var(--silver)] text-sm uppercase">
                    {row.entity}
                  </td>

                  <td className="px-4 py-2.5 text-[var(--silver)] text-sm">
                    {row.field || "-"}
                  </td>

                  <td className="px-4 py-2.5 text-sm">
                    {row.old_value != null || row.new_value != null ? (
                      <span className="text-[var(--silver)] break-all">
                        <span className="text-[var(--destructive)]">
                          {row.old_value ?? "∅"}
                        </span>
                        {" → "}
                        <span className="text-[#10B981]">
                          {row.new_value ?? "∅"}
                        </span>
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
